import { atom, getDefaultStore, useAtom, useAtomValue } from 'jotai'
import { atomWithImmer, useSetImmerAtom } from 'jotai-immer'
import { isNil } from 'lodash-es'
import type { Task } from 'src/services/apollo/generated/graphql'
import type { TaskSubmitFormValues } from '../../BaseTaskSubmitForm'
import { DEFAULT_I2VPRO_PARAMS, type I2vProDynamicModelItem } from './helper'
import { assignDefined } from './mergeFields'
import { buildItemsFromKindArrays } from './MultiReference/utils'
import {
  I2V_PRO_FORM_FIELDS,
  parseI2vTaskParameters,
  REFERENCE_VIDEO_FORM_FIELDS,
  type I2vTaskParametersPartial,
} from './parser'
import type { I2VProFormArgs } from './types'

const store = getDefaultStore()

export type ImageToVideoState = {
  lastResultTask?: Task
  lastResultTaskId?: string
  initialValue?: Partial<TaskSubmitFormValues>

  // An array of lora ids, with fixed weight = 0.7
  // TODO: We should use a Record<string, number> instead
  lora?: string[]
  // Selected action preset ids (v3.0 only)
  actionPresets?: string[]
  prefillPayload?: {
    task?: Task
    parameters?: I2vTaskParametersPartial
    initialValues?: Partial<TaskSubmitFormValues>
  }
}

export const imageToVideoStateAtom = atom<ImageToVideoState>({})
export function useSetImageToVideoState() {
  return useSetImmerAtom(imageToVideoStateAtom)
}
export function useImageToVideoStateValue() {
  return useAtomValue(imageToVideoStateAtom)
}

const imageToVideoFormStageAtom = atomWithImmer<I2VProFormArgs>(
  DEFAULT_I2VPRO_PARAMS,
)
export function useImageToVideoFormState() {
  return useAtom(imageToVideoFormStageAtom)
}

export function prefillImageToVideoPanel(args: {
  task: Pick<Task, 'id' | 'parameters' | 'outputs'>
}) {
  const { task } = args

  const parsedParameters = parseI2vTaskParameters(task.parameters)
  const formState: I2VProFormArgs = { ...DEFAULT_I2VPRO_PARAMS }

  assignDefined(formState, parsedParameters.i2vPro, I2V_PRO_FORM_FIELDS)

  /** Reference Video (Seedance 2.0) task shape — switch to multi-reference mode */
  const refVideo = parsedParameters.referenceVideo
  if (refVideo) {
    formState.refResourceMode = 'multiReference'
    assignDefined(formState, refVideo, REFERENCE_VIDEO_FORM_FIELDS)
    // `referenceVideo.prompt` (singular) renames to `prompts` (plural) on the
    // form, and `duration` widens to `number | string` upstream — coerce both
    // explicitly rather than via the generic merge.
    if (!isNil(refVideo.prompt)) formState.prompts = refVideo.prompt
    if (!isNil(refVideo.duration)) {
      formState.duration = String(
        refVideo.duration,
      ) as typeof formState.duration
    }
    const imageIds = refVideo.referenceImageMediaIds ?? []
    const videoIds = refVideo.referenceVideoMediaIds ?? []
    const audioIds = refVideo.referenceAudioMediaIds ?? []
    formState.multiRefResource = {
      imageMediaIds: imageIds,
      videoMediaIds: videoIds,
      audioMediaIds: audioIds,
      items: buildItemsFromKindArrays(imageIds, videoIds, audioIds),
    }
  }

  store.set(imageToVideoFormStageAtom, formState)

  const loraEntries = Object.entries(parsedParameters.lora ?? {}) as Array<
    [string, number]
  >
  const loraKeys = loraEntries.map(([key]) => key)
  const controlMediaId = parsedParameters.i2vPro?.controlMediaId

  const currentState = store.get(imageToVideoStateAtom)

  const nextInitialValue: Partial<TaskSubmitFormValues> = {
    ...(currentState?.initialValue ?? {}),
  }
  if (loraKeys.length) {
    const loraMap = Object.fromEntries(loraEntries) as Record<string, number>
    nextInitialValue.lora = loraMap
  } else {
    delete nextInitialValue.lora
  }

  const normalizedInitialValue =
    Object.keys(nextInitialValue).length > 0 ? nextInitialValue : undefined

  const pseudoParameters: Record<string, unknown> =
    typeof task.parameters === 'object' && task.parameters
      ? { ...(task.parameters as Record<string, unknown>) }
      : {}

  pseudoParameters.i2vPro = {
    ...formState,
    ...(parsedParameters.i2vPro ?? {}),
  }

  if (parsedParameters.extra) {
    pseudoParameters.extra = parsedParameters.extra
  }

  if (loraEntries.length > 0) {
    pseudoParameters.lora = Object.fromEntries(loraEntries) as Record<
      string,
      number
    >
  }

  if (!isNil(parsedParameters.width)) {
    pseudoParameters.width = parsedParameters.width
  }
  if (!isNil(parsedParameters.height)) {
    pseudoParameters.height = parsedParameters.height
  }
  if (!isNil(parsedParameters.priority)) {
    pseudoParameters.priority = parsedParameters.priority
  }
  if (!isNil(parsedParameters.sceneId)) {
    pseudoParameters.sceneId = parsedParameters.sceneId
  }

  const now = new Date().toISOString()
  const id = task.id.length > 0 ? task.id : 'imported'

  const pseudoTask: Task = {
    __typename: 'Task',
    id,
    createdAt: now,
    updatedAt: now,
    retryCount: 0,
    userId: 'imported',
    parameters: pseudoParameters,
    outputs: task.outputs,
  }

  store.set(imageToVideoStateAtom, {
    ...currentState,
    lora: loraKeys,
    actionPresets: controlMediaId ? [controlMediaId] : [],
    initialValue: normalizedInitialValue,
    lastResultTask: pseudoTask,
    lastResultTaskId: pseudoTask.id,
    prefillPayload: {
      task: pseudoTask,
      parameters: parsedParameters,
      initialValues:
        loraEntries.length > 0
          ? {
              lora: Object.fromEntries(loraEntries) as Record<string, number>,
            }
          : undefined,
    },
  })
}

/**
 * Clears the last-imported/generated task pointer without wiping the user's
 * current form inputs. Call when the user explicitly drops their primary
 * input (e.g. removes the start frame thumbnail). The in-flight result
 * preview no longer corresponds to the working inputs.
 */
export function clearI2vTaskResult() {
  store.set(imageToVideoStateAtom, prev => ({
    ...prev,
    lastResultTask: undefined,
    lastResultTaskId: undefined,
    prefillPayload: undefined,
  }))
}

/**
 * Apply a deeplinked model directly to the Jotai store, bypassing React state
 * setters. Used by the parent component's effect so URL imports can seed the
 * panel before child form effects run.
 */
export function applyUrlModelToPanel(model: I2vProDynamicModelItem) {
  const current = store.get(imageToVideoFormStageAtom)
  if (current.model === model.model) return

  const modeDefault = model.panelConfig?.mode?.default
  store.set(imageToVideoFormStageAtom, {
    ...current,
    model: model.model,
    ...(modeDefault ? { mode: modeDefault } : {}),
    refResourceMode: model.panelConfig?.multiReference
      ? 'multiReference'
      : undefined,
  })

  const state = store.get(imageToVideoStateAtom)
  if (state.actionPresets && state.actionPresets.length > 0) {
    store.set(imageToVideoStateAtom, { ...state, actionPresets: [] })
  }
}

/** Reset both i2v atoms to their initial values when the panel unmounts. */
export function resetImageToVideoPanel() {
  store.set(imageToVideoFormStageAtom, DEFAULT_I2VPRO_PARAMS)
  store.set(imageToVideoStateAtom, {})
}
